import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  findProject,
  RIPPLES_PASSWORD,
  RIPPLES_DESTINATION_ENC,
} from "@/data/projects";
import { getProjectDetail } from "@/data/projectDetails";
import { getProjectGallery } from "@/data/projectGalleries";
import ProjectDetail from "@/pages/ProjectDetail";
import ProjectGallery from "@/pages/ProjectGallery";
import "@/pages/WorkProject.css";

export default function WorkProject() {
  const { slug } = useParams();
  const project = findProject(slug);
  const detail = getProjectDetail(slug);
  const gallery = getProjectGallery(slug);

  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (project && !detail && !gallery) {
      document.title = `${project.name} — Ōraṇi`;
    }
  }, [project, detail, gallery]);

  if (!project) return <Navigate to="/work" replace />;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw.trim().toLowerCase() === RIPPLES_PASSWORD) {
      // Decode the obfuscated destination only at the moment of a
      // valid match, then send the visitor straight to the hosted
      // gallery. Using window.location.replace so this URL doesn't
      // appear in the back-button history.
      try {
        const dest = atob(RIPPLES_DESTINATION_ENC);
        window.location.replace(dest);
      } catch {
        setError(true);
      }
    } else {
      setError(true);
    }
  };

  // Editorial case-study delegate (e.g. In The Warm After)
  if (detail && !project.private) {
    return <ProjectDetail project={detail} />;
  }

  // Visual-study (non-case-study) gallery delegate (e.g. Tie and Untie)
  if (gallery && !project.private) {
    return <ProjectGallery project={gallery} />;
  }

  // Password gate for private projects
  if (project.private) {
    return (
      <main className="wp-gate" data-testid="work-project-gate">
        <Link to="/work" className="wp-gate__back" data-testid="wp-gate-back">
          <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
            <path
              d="M21 5H1M5 1L1 5l4 4"
              stroke="currentColor"
              strokeWidth="1"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Selected Work
        </Link>

        <Link
          to="/work/in-the-warm-after"
          className="wp-gate__next"
          data-testid="wp-gate-next-project"
          aria-label="Next project: In The Warm After"
        >
          Next — In The Warm After
          <span className="wp-gate__next-rule" aria-hidden="true" />
        </Link>

        <Link
          to="/work/gurukul"
          className="wp-gate__prev"
          data-testid="wp-gate-prev-project"
          aria-label="Previous project: Gurukul"
        >
          <span className="wp-gate__prev-rule" aria-hidden="true" />
          Prev — Gurukul
        </Link>

        <div className="wp-gate__inner">
          <span className="wp-gate__eyebrow">Private Access</span>
          <h1 className="wp-gate__title">{project.name}</h1>
          <p className="wp-gate__desc">
            This project is currently under publication embargo. Enter password
            to view.
          </p>

          <form className="wp-gate__form" onSubmit={handleSubmit}>
            <label className="wp-gate__label" htmlFor="ripples-pw">
              Password
            </label>
            <input
              id="ripples-pw"
              type="password"
              className={"wp-gate__input" + (error ? " is-error" : "")}
              placeholder="Password"
              value={pw}
              onChange={(e) => {
                setPw(e.target.value);
                if (error) setError(false);
              }}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              data-testid="wp-gate-input"
            />
            {error && (
              <span className="wp-gate__error" data-testid="wp-gate-error">
                That password does not match.
              </span>
            )}
            <button
              type="submit"
              className="wp-gate__submit"
              data-testid="wp-gate-submit"
            >
              Enter
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main
      className="wp"
      data-testid={`work-project-${project.slug}`}
      aria-label={project.name}
    >
      <Link to="/work" className="wp__back" data-testid="wp-back">
        <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
          <path
            d="M21 5H1M5 1L1 5l4 4"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Selected Work
      </Link>

      <header className="wp__header">
        <span className="wp__eyebrow">
          {project.type} <span aria-hidden="true">/</span> {project.location}
        </span>
        <h1 className="wp__title">{project.name}</h1>
        <p className="wp__desc">{project.descriptor}</p>
      </header>

      <figure className="wp__hero">
        <img src={project.image} alt={project.name} className="wp__image" />
      </figure>

      <section className="wp__body">
        <p className="wp__placeholder">
          The full case study for this project is being prepared. In the
          meantime, please reach out to the studio to view the complete work.
        </p>
        <Link to="/work" className="wp__return" data-testid="wp-return">
          Return to Selected Work
        </Link>
      </section>
    </main>
  );
}
